import React, { useCallback, useEffect, useState } from 'react';

import { Carousel, CarouselContent, CarouselItem, Stack } from '@crossnokaye/ux-common';

type CarouselApi = Parameters<NonNullable<React.ComponentProps<typeof Carousel>['setApi']>>[0];

interface ActionSidebarTemplateProps {
  animaticItems: React.ReactNode[];
  selectedIndex: number;
  onIndexChange: (index: number) => void;
  shelf: React.ReactNode;
  actions: React.ReactNode;
}

export const ActionSidebarTemplate = ({
  animaticItems,
  selectedIndex,
  onIndexChange,
  shelf,
  actions,
}: ActionSidebarTemplateProps): React.ReactNode => {
  const [api, setApi] = useState<CarouselApi>();

  // Sync carousel to selected index when it changes externally
  useEffect(() => {
    if (!api) return;
    if (api.selectedScrollSnap() !== selectedIndex) {
      api.scrollTo(selectedIndex);
    }
  }, [api, selectedIndex]);

  // Listen for carousel scroll events and notify parent
  const handleSelect = useCallback(() => {
    if (!api) return;
    const newIndex = api.selectedScrollSnap();
    if (newIndex !== selectedIndex) {
      onIndexChange(newIndex);
    }
  }, [api, selectedIndex, onIndexChange]);

  useEffect(() => {
    if (!api) return;
    api.on('select', handleSelect);
    return () => {
      api.off('select', handleSelect);
    };
  }, [api, handleSelect]);

  return (
    <Stack className="items-center">
      <Stack className="w-full items-center" gap="sm">
        <div className="w-full hidden @2xl:flex flex-1 justify-center">
          {animaticItems[selectedIndex]}
        </div>
        <Carousel
          className="w-full @2xl:hidden"
          opts={{ align: 'center', loop: true, containScroll: false, startIndex: selectedIndex }}
          setApi={setApi}
        >
          <CarouselContent>
            {animaticItems.map((animatic, index) => (
              <CarouselItem
                key={index}
                className="basis-[70%] @md:basis-[67%] @lg:basis-[64%] @xl:basis-[58%] flex justify-center"
              >
                {animatic}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {shelf}
      </Stack>
      <Stack className="w-full">{actions}</Stack>
    </Stack>
  );
};
